import * as React from "react";

import type {
  AppContextualMenuProps,
  AppContextualMenuPropsNext,
} from "@1js/acui-menu";
import { AppContextualMenu } from "@1js/acui-menu";

import type {
  AppFlyoutAnchorProps,
  AppFlyoutAnchorState,
  AppMenuButtonProps,
} from "./AppFlyoutAnchor.types";
import { useAppFlyoutAnchorState } from "./useAppFlyoutAnchorState";
import { AppMenuButton } from ".";
import { slot, useMergedRefs } from "@fluentui/react-components";
import { useShadowRenderedMenu } from "./useShadowRenderedMenu";

export const useAppFlyoutAnchor = (
  props: AppFlyoutAnchorProps,
  ref: React.Ref<HTMLButtonElement>
): AppFlyoutAnchorState => {
  const {
    shadowRenderMenu,
    menuDefinition,
    deferredMenuDefinition,
    useLegacyContextMenu,
    interactivityTrackerContext,
    alwaysRestoreFocus,
  } = props;

  const menuButtonRef = React.useRef<HTMLButtonElement>(null);
  const defaultMenuButtonProps = {
    ...props,
    ref: useMergedRefs(ref, menuButtonRef),
  } as AppMenuButtonProps;

  const shadowRenderedMenuState = useShadowRenderedMenu(props, "Flyout");
  const defaultMenuProps = shadowRenderedMenuState.getMenuProps(menuDefinition);

  const appContextualMenuPropsLegacy: AppContextualMenuProps &
    Partial<AppContextualMenuPropsNext> = {
    ...defaultMenuProps,
    alwaysRestoreFocus,
    isV9ContextMenuEnabled: props.isV9ContextMenuEnabled,
    isV9FloatieEnabled: props.isV9FloatieEnabled,
    isV9MenuEnabled: props.isV9MenuEnabled,
    ribbonMode: props.ribbonMode,
    alignV9MenuToEnd: props.alignV9MenuToEnd,
  };

  const appContextualMenuProps: AppContextualMenuProps &
    Partial<AppContextualMenuPropsNext> = {
    ...defaultMenuProps,
    useLegacyContextMenu,
    interactivityTrackerContext,
    alwaysRestoreFocus,
    isV9ContextMenuEnabled: props.isV9ContextMenuEnabled,
    isV9FloatieEnabled: props.isV9FloatieEnabled,
    isV9MenuEnabled: props.isV9MenuEnabled,
    ribbonMode: props.ribbonMode,
    alignV9MenuToEnd: props.alignV9MenuToEnd,
  };

  const isNextContextMenuEnabled = props.isV9ContextMenuEnabled;

  const state: AppFlyoutAnchorState = {
    shadowRenderMenu,
    useLegacyContextMenu,
    ...shadowRenderedMenuState,

    components: {
      menuButton: AppMenuButton,
      menu: deferredMenuDefinition?.menuAs ?? AppContextualMenu,
    },

    menu: slot.always(props.menu, {
      defaultProps:
        !isNextContextMenuEnabled || deferredMenuDefinition?.menuAs
          ? appContextualMenuPropsLegacy
          : appContextualMenuProps,
      elementType: deferredMenuDefinition?.menuAs ?? AppContextualMenu,
    }),
    menuButton: slot.always(props.menuButton, {
      defaultProps: defaultMenuButtonProps,
      elementType: AppMenuButton,
    }),
  };
  state.menuButton.onKeyDownCapture = props.onKeyDown;
  return useAppFlyoutAnchorState(props, state);
};
